/**
 * Database Adapter Interface
 *
 * Abstracts the data layer for the database editor so it can work with
 * different backends (SQLite, Firebird, etc.) without UI changes.
 *
 * Each adapter implements this interface to handle:
 * - Authentication (if required)
 * - Schema introspection
 * - Table data fetching with filters
 * - SQL query execution
 * - Cell/row mutations
 * - Table DDL operations
 */
import { request, fetchRaw, currentSessionSignal, sessionEndedSince, dropIfSessionEnded, isSessionEndedError, captureRequestIdentity, type RequestOptions } from '@papan/api-client'

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface ColumnInfo {
  name: string
  type: string
  notnull: boolean
  pk: boolean
  defaultValue: string | null
}

export interface ForeignKey {
  from: string
  table: string
  to: string
}

export interface TableInfo {
  type: string
  name: string
  sql: string | null
  columns: ColumnInfo[]
  foreignKeys: ForeignKey[]
  rowCount: number
}

export interface QueryResult {
  columns: string[]
  types?: string[]
  rows: Record<string, unknown>[]
  rowCount?: number
  totalCount?: number
  duration?: number
  changes?: number
  lastInsertRowid?: string
  primaryKeys?: string[]
  error?: string
}

/** Transpose array-based rows to object rows (server sends arrays for bandwidth, UI needs objects) */
function transposeRows(res: { columns: string[]; rows: unknown[][]; _rowIdIndex?: number; [k: string]: unknown }): Record<string, unknown>[] {
  const { columns, rows, _rowIdIndex } = res
  return rows.map((row: unknown[]) => {
    const obj: Record<string, unknown> = {}
    for (let i = 0; i < columns.length; i++) obj[columns[i]] = row[i]
    if (_rowIdIndex != null && _rowIdIndex >= 0 && row[_rowIdIndex] != null) {
      obj.__rowid = row[_rowIdIndex]
    }
    return obj
  })
}

export type FilterOperator =
  | 'equals' | 'not_equals'
  | 'contains' | 'starts_with' | 'ends_with'
  | 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal'
  | 'is_empty' | 'is_not_empty'

export interface TableFilter {
  id: string
  column: string
  operator: FilterOperator
  value: string
  logic?: 'and' | 'or'
}

export interface CellConflict {
  table: string
  column: string
  rowid: number
  oldValue: unknown
  newValue: unknown
  currentValue: unknown
  reason: 'value_changed' | 'row_deleted'
}

export interface GetTableOptions {
  versionId?: string
  editMode?: boolean
  filters?: TableFilter[]
  rawWhere?: string
  columns?: string[]
}

export interface CellUpdateResult {
  ok?: boolean
  conflict?: boolean
  reason?: string
  currentValue?: unknown
}

export interface CellSaveResult {
  ok: boolean
  error?: string
  results: Array<{ column: string; rowid: number; ok: boolean; error?: string }>
}

export interface RowInsertResult {
  ok: boolean
  error?: string
  rowid?: number
}

export interface TableModifyAction {
  column?: string
  newName?: string
  type?: string
  notnull?: boolean
  defaultValue?: string
}

// ---------------------------------------------------------------------------
// Adapter interface
// ---------------------------------------------------------------------------

export interface DbAdapter {
  /** Adapter identifier (e.g., 'sqlite', 'firebird') */
  readonly type: string

  /** Whether the database requires authentication to open */
  readonly requiresAuth: boolean

  /** Authenticate with the database. Credentials stored in httpOnly cookie by server. */
  authenticate?(fileId: string, user: string, password: string, rememberMe?: boolean): Promise<{ ok: boolean }>
  /** Clear stored credentials for a file */
  logout?(fileId: string): Promise<{ ok: boolean }>

  // -- Schema --
  getSchema(fileId: string, opts?: { versionId?: string }): Promise<{ schema: TableInfo[] }>

  // -- Table data --
  getTable(fileId: string, tableName: string, limit: number, offset: number, opts?: GetTableOptions): Promise<QueryResult>

  // -- SQL queries --
  query(fileId: string, sql: string, opts?: { versionId?: string }): Promise<QueryResult>
  execute(fileId: string, sql: string): Promise<QueryResult>

  // -- Cell mutations --
  cellUpdate(fileId: string, table: string, column: string, rowid: number, newValue: unknown, oldValue: unknown): Promise<CellUpdateResult>
  cellSave(fileId: string, table: string, updates: Array<{ column: string; rowid: number | string; newValue: unknown }>): Promise<CellSaveResult>

  // -- Row mutations --
  rowDelete(fileId: string, table: string, rowids: number[]): Promise<{
    ok: boolean
    error?: string
    /** How many rows went.
     *
     *  `null` is NOT the same as absent, and `classifyRowDelete` turns on the
     *  difference. Absent is an older backend that never reported it, and the
     *  caller treats `ok` as all-succeeded. An explicit null is this backend
     *  saying it RAN the deletes and cannot report on them, and the caller
     *  must not infer anything from it. Firebird sends it two ways: its
     *  driver refusing a rowcount, or a row going that the server cannot
     *  name back. Spelled out because this field already cost a round over a
     *  comment that read true and was not. Declared `number` alone, the value the whole
     *  branch turns on looked impossible here while the server really sends
     *  it, and `res.deleted ?? 0` would have type-checked. */
    deleted?: number | null
    /** WHICH rows went. Absent, or null, means the backend could not say;
     *  the caller then falls back to `deleted` (see `classifyRowDelete`).
     *  Never infer it from
     *  what is still on screen: the grid is paged and filtered, so absence
     *  there is not deletion.
     *
     *  `number | string`, not `number[]`, and that is not pedantry: Firebird's
     *  `__rowid` is a JSON-encoded PK tuple — a STRING — while SQLite's is a
     *  real rowid. These values are compared by Set membership to clear
     *  delete marks, so `new Set<number>(deletedIds)` would type-check, purge
     *  nothing on Firebird, and leave marks aimed at rows already gone. */
    deletedIds?: Array<number | string> | null
  }>
  rowInsert(fileId: string, table: string, values?: Record<string, unknown>, functions?: Record<string, string>): Promise<RowInsertResult>
  /** Insert several new rows in ONE request — all or nothing, and the whole Save records ONE version (#295 step 2:
   *  a large db is copied once per Save, not once per row). Optional: an adapter without it is looped per row. */
  rowInsertMany?(fileId: string, table: string, rows: Array<{ values?: Record<string, unknown>; functions?: Record<string, string> }>): Promise<{ ok: boolean; error?: string; rowids?: number[] }>

  // -- Table DDL --
  tableDrop(fileId: string, table: string): Promise<{ ok: boolean; error?: string }>
  tableCreate(fileId: string, table: string, columns: Array<{ name: string; type: string; pk?: boolean; notnull?: boolean; defaultValue?: string; unique?: boolean }>): Promise<{ ok: boolean; error?: string }>
  tableDuplicate(fileId: string, table: string, newName: string): Promise<{ ok: boolean; error?: string }>
  tableModify(fileId: string, table: string, action: string, opts: TableModifyAction): Promise<{ ok: boolean; error?: string }>

  // -- Lazy schema (optional) --
  getTableInfo?(fileId: string, tableName: string): Promise<{
    columns: ColumnInfo[]
    foreignKeys: ForeignKey[]
  }>

  // -- Full table export --
  exportTable?(fileId: string, table: string, format: 'csv' | 'json' | 'parquet', opts?: { columns?: string[]; where?: string }): Promise<Blob>

  // -- Session management --
  flush?(fileId: string): Promise<{ flushed?: boolean; conflict?: boolean }>
  discard?(fileId: string): Promise<void>
}

// ---------------------------------------------------------------------------
// SQLite adapter
// ---------------------------------------------------------------------------

/**
 * The grid's column subset on a `/table` request: one `col` per name, since a
 * name may contain a comma, which a comma-joined `columns=` cannot carry (the
 * server decodes, then splits — I2). The server still reads the old
 * `columns=` from a client built before this. The other way round, an API
 * not yet running this change ignores `col` and answers EVERY column until
 * it is deployed — wider than asked, never wrong rows or an error.
 */
export function columnSubsetQuery(columns: string[] | undefined): string {
  return columns && columns.length > 0 ? columns.map(c => `&col=${encodeURIComponent(c)}`).join('') : ''
}

function vq(versionId?: string): string {
  return versionId ? `&version=${versionId}` : ''
}

// ---------------------------------------------------------------------------
// Detached SQLite writes (papan-ai/papan#154)
//
// A slow write/DDL — multi-GB CREATE TABLE AS SELECT, a big CREATE INDEX, a
// bulk UPDATE — no longer dies with its request. The route answers 202 with a
// job id once it stops holding the connection, and the outcome is polled.
//
// The polling lives HERE, inside the adapter, so `DbAdapter` keeps its exact
// shape (`Promise<result>`) and the DB-editor UI needs no changes at all. The
// alternative — teaching every caller about a two-phase write — would have put
// the same "did it finish?" logic in ten components, each free to get it wrong.
// ---------------------------------------------------------------------------

interface QueuedWrite { queued: true; jobId: string }

function isQueued(res: unknown): res is QueuedWrite {
  return !!res && typeof res === 'object'
    && (res as QueuedWrite).queued === true
    && typeof (res as QueuedWrite).jobId === 'string'
}

/**
 * Poll a detached write to its terminal state.
 *
 * The deadline is generous (matching the server's own exec ceiling) because the
 * whole point is work that legitimately takes minutes. Hitting it does NOT mean
 * the write failed — it means we stopped watching — so the message says exactly
 * that rather than reporting a failure for something still running. Reporting
 * running work as failed is the bug this issue exists to fix; re-introducing it
 * in the client would be the same defect one layer out.
 */
/** #1316 review round 1: a loop's session ended — fail as a dropped answer (session-ended, toasts held off). */
const sessionEndedFailure = <T>(start: AbortSignal | undefined): Promise<T> => dropIfSessionEnded(Promise.resolve(undefined as T), start)

async function pollSqliteWrite<T>(jobId: string, fileId?: string): Promise<T> {
  const POLL_DEADLINE_MS = 10 * 60 * 1000
  const startedAt = Date.now()
  // #1316 review round 1 (D-I2): the poll belongs to the session that started it — after it ended no status read is
  // sent (it would go out as the NEXT identity: a 404 → "result no longer available" there, or the old write's result
  // applied); it fails as a dropped answer
  const start = currentSessionSignal()
  let delay = 300
  while (true) {
    await new Promise(r => setTimeout(r, delay))
    if (sessionEndedSince(start)) return sessionEndedFailure<T>(start)
    // Backs off to 2s: a multi-minute DDL should not be polled 600 times.
    delay = Math.min(delay * 1.5, 2000)
    let status: {
      state: 'queued' | 'running' | 'completed' | 'failed' | 'expired'
      result?: T
      error?: string
    }
    try {
      status = await request(`/api/sqlite/write-status/${jobId}`)
    } catch (err) {
      if (isSessionEndedError(err)) throw err
      // A 404 means the job is unknown or its record expired — which does NOT
      // say the write failed. Most likely it succeeded and we are simply too
      // late to read the outcome, so say that rather than inventing a verdict.
      // (An outcome the server CAN report, including a genuine failure or an
      // expired result, comes back as a 200 with a `state` — the status
      // describes the poll, the body describes the write.)
      if ((err as { status?: number })?.status === 404) throw new Error(LOST_RESULT_MESSAGE)
      throw err
    }
    if (status.state === 'completed') {
      // A completed job with no result is an anomaly, not a clean empty
      // outcome — surface it rather than returning a silent success.
      if (status.result === undefined) {
        throw new Error('The operation finished but returned no result — refresh to verify.')
      }
      // This poll's own version save failed: keep retrying it in the background (#846).
      // …unless the server ended it unsaved and told the person (`versionUnsavedNotified`) — nothing left to retry
      if ((status as { versionSaved?: boolean }).versionSaved === false && (status as { versionUnsavedNotified?: boolean }).versionUnsavedNotified !== true) void retryUnsavedVersion(jobId, undefined, fileId)
      return status.result
    }
    if (status.state === 'failed') {
      const code = (status as { code?: unknown }).code
      throw Object.assign(new Error(status.error || 'Query execution failed'), typeof code === 'string' ? { code } : {})
    }
    if (status.state === 'expired') throw new Error(LOST_RESULT_MESSAGE)
    if (Date.now() - startedAt > POLL_DEADLINE_MS) {
      throw new Error('This is taking longer than expected — it is still running in the background. Refresh to see the result.')
    }
  }
}

const LOST_RESULT_MESSAGE =
  'The result is no longer available — the operation most likely completed. Refresh to see the current state.'

/**
 * A write that LANDED but whose version the server could not record (#846): the answer carries
 * `versionSaved: false` and the job id whose record the server kept. Polling that job's status
 * re-runs the version save; without it the change reaches the history only with the db's next
 * recorded save (each version is the whole file — E7 PR 2 §3.2, D4; no watcher backstop since slice 2).
 */
function unsavedVersionJob(res: unknown): string | null {
  if (!res || typeof res !== 'object') return null
  const r = res as { versionSaved?: unknown; jobId?: unknown }
  return r.versionSaved === false && typeof r.jobId === 'string' ? r.jobId : null
}

/** Delays between the background retries of an unsaved version — then the db's next recorded save carries it. */
export const UNSAVED_VERSION_RETRY_DELAYS_MS = [1_000, 5_000, 20_000]

/**
 * Window event raised when a landed write's version is STILL unsaved after the background
 * retries — every retry used, or one refused for a reason a retry won't change (a full drive).
 * The DB editor listens and tells the person (I14): the change is in the db, but its history
 * entry comes only with the db's next recorded save (E7 D4). `detail.fileId` names the db.
 */
export const DB_VERSION_UNSAVED_EVENT = 'papan:db-version-unsaved'
/** Why the version is still unsaved, when known: `in-use` = the db stayed busy (H15 W6 — a SQLite lock,
 *  possibly a sandbox's, or another change's turn), so the person isn't told their drive may be full;
 *  `superseded` = the server answered `versionUnsavedReason: 'superseded'` (E7 PR 2 slice 2: a newer drive version was
 *  saved meanwhile and replaces the change — it will NOT come with the next recorded save; reported at once, no retry). */
export type DbVersionUnsavedReason = 'in-use' | 'superseded'
function reportVersionUnsaved(fileId: string | undefined, reason?: DbVersionUnsavedReason, start?: AbortSignal): void {
  if (start && sessionEndedSince(start)) return   // the person's session ended: the notice would land in another one
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return
  window.dispatchEvent(new CustomEvent(DB_VERSION_UNSAVED_EVENT, { detail: { fileId, ...(reason ? { reason } : {}) } }))
}

/**
 * Retry an unsaved version in the background: the write already succeeded and was reported, so
 * nothing here is shown or thrown. Stops once a poll answers without `versionSaved: false`.
 */
export async function retryUnsavedVersion(jobId: string, delays = UNSAVED_VERSION_RETRY_DELAYS_MS, fileId?: string): Promise<void> {
  // #1316 review round 1 (G2 follow-up): must-finish work, like the undo — it runs as the identity that made the edit
  // (pinned at its start, exempt from the session drop), never as the next one; a notice is not shown in a later session
  const start = currentSessionSignal()
  const identity = captureRequestIdentity()
  for (const ms of delays) {
    await new Promise(r => setTimeout(r, ms))
    try {
      const status = await request<{ state?: string; versionSaved?: boolean; versionUnsavedNotified?: boolean }>(`/api/sqlite/write-status/${jobId}`, { identity })
      if (status?.versionSaved !== false) return
      if (status.versionUnsavedNotified === true) return // the server ended it and told the person
    } catch {
      return // record gone / not ours — nothing left to retry
    }
  }
  reportVersionUnsaved(fileId, undefined, start)
}

/**
 * An in-process write (grid edit, SQL console) has no job to poll: retry its unsaved version
 * through `/flush`, which records the db's current bytes as a version by the caller — in the org
 * that owns the db. Same delays; stops at the first success.
 *
 * Accepted trade-off: the flush records the CURRENT bytes, so if another person's write also
 * failed to version in the seconds between, their change rides in this caller's version (it would
 * otherwise wait for the db's next recorded save). History only, never a permission.
 */
export async function retryUnsavedVersionByFlush(fileId: string, delays = UNSAVED_VERSION_RETRY_DELAYS_MS, writeId?: string): Promise<void> {
  let busy = false
  // E7 PR 2 slice 2: the write's id (`versionWriteId`) — the server races its own retry against these for ONE outcome:
  // a `settled` answer means the other chain recorded it (saved, or told the person) — nothing to retry or tell.
  const qs = writeId ? `?writeId=${encodeURIComponent(writeId)}&notice=client` : '?notice=client'
  // #1316 review round 1 (D-I2): the retries belong to the session of the write — a flush after it ended would record
  // a version credited to the NEXT identity (or tell it of a version it never wrote): stop quietly
  const start = currentSessionSignal()
  const identity = captureRequestIdentity()   // the flush is recorded as the identity that made the edit, whatever session is current
  for (const ms of delays) {
    await new Promise(r => setTimeout(r, ms))
    try {
      await request(`/api/sqlite/${fileId}/flush${qs}`, { method: 'POST', identity })
      return
    } catch (err) {
      const status = (err as { status?: number })?.status
      // H15 W6: a busy db (409 DB_BUSY — a SQLite lock, possibly a sandbox's, or another change's
      // turn) is worth the next attempt, and if it never frees the person is told THAT, not "full".
      busy = status === 409 && (err as { code?: string })?.code === 'DB_BUSY'
      // E7 PR 2 slice 2: the db's row has a live mirror intent — a newer drive version replaces the change; final,
      // and told as THAT (never "in use")
      if (busy && (err as { versionUnsavedReason?: string })?.versionUnsavedReason === 'superseded') {
        reportVersionUnsaved(fileId, 'superseded', start)
        return
      }
      if (busy) continue
      // Not allowed / gone — nothing to retry; a timeout or rate limit is worth the next attempt.
      // A refusal still leaves the change without a version: tell the person, unless the db is
      // gone or no longer theirs to write (404 / 403 — nothing of theirs is left unrecorded).
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        if (status !== 403 && status !== 404) reportVersionUnsaved(fileId, undefined, start)
        return
      }
    }
  }
  reportVersionUnsaved(fileId, busy ? 'in-use' : undefined, start)
}

/** Start the right background retry for a write that landed with `versionSaved: false`. */
function retryIfUnsaved(fileId: string, res: unknown): void {
  if (!res || typeof res !== 'object' || (res as { versionSaved?: unknown }).versionSaved !== false) return
  // The server ended this write unsaved AND told the person (`versionUnsavedNotified`): nothing to retry or tell
  if ((res as { versionUnsavedNotified?: unknown }).versionUnsavedNotified === true) return
  // E7 PR 2 slice 2: superseded — final on the server; a retry would meet the same mirror intent (and read as "in use")
  if ((res as { versionUnsavedReason?: unknown }).versionUnsavedReason === 'superseded') { reportVersionUnsaved(fileId, 'superseded'); return }
  const jobId = unsavedVersionJob(res)
  const writeId = (res as { versionWriteId?: unknown }).versionWriteId
  void (jobId ? retryUnsavedVersion(jobId, undefined, fileId) : retryUnsavedVersionByFlush(fileId, undefined, typeof writeId === 'string' ? writeId : undefined))
}

/** A grid write — returns the result, retrying an unsaved version in the background. */
/**
 * E7 PR 2 slice 2: every version-saving request from the DB editor says it shows the version notice itself
 * (`notice=client`), so the server does not also report an ending this request answers (one notice per write).
 */
export function withClientNotice(path: string): string {
  return `${path}${path.includes('?') ? '&' : '?'}notice=client`
}


async function gridWrite<T>(fileId: string, path: string, body: unknown): Promise<T> {
  let res = await request<T | QueuedWrite>(withClientNotice(path), { method: 'POST', body: JSON.stringify(body) })
  // #295 step 2: grid writes run in the sandbox's write job — one still running when the server's wait ended comes
  // back as a detached write; follow it to its outcome (the server records its version when it ends)
  if (isQueued(res)) res = await pollSqliteWrite<T>(res.jobId, fileId)
  retryIfUnsaved(fileId, res)
  return res as T
}

/** POST a write route, transparently following a 202 to its outcome. */
export async function sqliteWrite<T>(path: string, body: unknown): Promise<T> {
  const fileId = /^\/api\/sqlite\/([^/]+)\//.exec(path)?.[1]
  const res = await request<T | QueuedWrite>(withClientNotice(path), { method: 'POST', body: JSON.stringify(body) })
  if (isQueued(res)) return pollSqliteWrite<T>(res.jobId, fileId)
  // E7 PR 2 slice 2: superseded by a newer drive version — final on the server (no job id to poll)
  if ((res as { versionUnsavedReason?: unknown } | null)?.versionUnsavedReason === 'superseded') {
    // …unless the server's finalizer already told the person (it won the job's claim: `versionUnsavedNotified`)
    if ((res as { versionUnsavedNotified?: unknown }).versionUnsavedNotified !== true) reportVersionUnsaved(fileId, 'superseded')
    return res as T
  }
  const unsaved = unsavedVersionJob(res)
  if (unsaved) void retryUnsavedVersion(unsaved, undefined, fileId)
  return res as T
}

export const sqliteAdapter: DbAdapter = {
  type: 'sqlite',
  requiresAuth: false,

  async getSchema(fileId, opts) {
    return request(`/api/sqlite/${fileId}/schema?_=1${vq(opts?.versionId)}`)
  },

  async getTable(fileId, tableName, limit, offset, opts) {
    const mode = opts?.editMode ? '&mode=edit' : ''
    const cols = columnSubsetQuery(opts?.columns)
    const qs = `?limit=${limit}&offset=${offset}${vq(opts?.versionId)}${mode}${cols}`

    let res: any
    if (opts?.filters && opts.filters.length > 0) {
      res = await request(`/api/sqlite/${fileId}/table/${encodeURIComponent(tableName)}${qs}`, {
        method: 'POST',
        body: JSON.stringify({ filters: opts.filters }),
      })
    } else {
      const where = opts?.rawWhere ? `&where=${encodeURIComponent(opts.rawWhere)}` : ''
      res = await request(`/api/sqlite/${fileId}/table/${encodeURIComponent(tableName)}${qs}${where}`)
    }
    // Transpose array rows to object rows if server sent arrays
    if (res.rows?.length > 0 && Array.isArray(res.rows[0])) {
      res.rows = transposeRows(res)
    }
    return res
  },

  async query(fileId, sql, opts) {
    let res: any = await request(withClientNotice(`/api/sqlite/${fileId}/query?_=1${vq(opts?.versionId)}`), { method: 'POST', body: JSON.stringify({ sql }) })
    // E7 PR 2 §3.2: a statement still running when the server's wait ended comes back as a detached write — follow
    // it to its outcome (the server records its version when it ends).
    if (isQueued(res)) res = await pollSqliteWrite<any>(res.jobId, fileId)
    retryIfUnsaved(fileId, res)
    if (res.rows?.length > 0 && Array.isArray(res.rows[0])) {
      res.rows = transposeRows(res)
    }
    return res
  },

  async execute(fileId, sql) {
    return sqliteWrite(`/api/sqlite/${fileId}/execute`, { sql })
  },

  async cellUpdate(fileId, table, column, rowid, newValue, oldValue) {
    return gridWrite(fileId, `/api/sqlite/${fileId}/cell`, { table, column, rowid, newValue, oldValue })
  },

  async cellSave(fileId, table, updates) {
    return gridWrite(fileId, `/api/sqlite/${fileId}/cell-save`, { table, updates })
  },

  async rowDelete(fileId, table, rowids) {
    return gridWrite(fileId, `/api/sqlite/${fileId}/row-delete`, { table, rowids })
  },

  async rowInsert(fileId, table, values, functions) {
    return gridWrite(fileId, `/api/sqlite/${fileId}/row-insert`, { table, values, functions })
  },

  async rowInsertMany(fileId, table, rows) {
    return gridWrite(fileId, `/api/sqlite/${fileId}/row-insert`, { table, rows })
  },

  async tableDrop(fileId, table) {
    return sqliteWrite(`/api/sqlite/${fileId}/table-drop`, { table })
  },

  async tableCreate(fileId, table, columns) {
    return sqliteWrite(`/api/sqlite/${fileId}/table-create`, { table, columns })
  },

  async tableDuplicate(fileId, table, newName) {
    return sqliteWrite(`/api/sqlite/${fileId}/table-duplicate`, { table, newName })
  },

  async tableModify(fileId, table, action, opts) {
    return sqliteWrite(`/api/sqlite/${fileId}/table-modify`, { table, action, ...opts })
  },

  async exportTable(fileId, table, format, opts) {
    const res = await fetchRaw(`/api/sqlite/${fileId}/export-table`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ table, format, columns: opts?.columns, where: opts?.where }),
    })
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export failed')
    return res.blob()
  },

  async flush(fileId) {
    return request(`/api/sqlite/${fileId}/flush`, { method: 'POST' })
  },

  async discard(fileId) {
    return request(`/api/sqlite/${fileId}/discard`, { method: 'POST' })
  },
}

// ---------------------------------------------------------------------------
// Firebird adapter
// ---------------------------------------------------------------------------

/**
 * How long the editor waits for a Firebird job (#296 D1 (a)) — every route that runs one in the sandbox. Every read
 * first copies the whole `.fdb` on the server, and the server gives that copy extra time by the file's size
 * (`fdbSnapshotCopyAllowanceMs`) — a 1 GiB file takes ~15 s before the read even starts; a write may first wait up to
 * 20 s for the file's turn behind such a read, and the server answers it only after 50 s (DB_BUSY "nothing was changed"
 * / outcome_unknown). The api client's default bounds (8 s + one retry for a GET, 30 s for a POST) would give up first:
 * a GET's retry started a second full copy, and a write abandoned at 30 s could still commit — "failed", saved again,
 * a duplicate row. So a job carries its own bound: the edge's 100 s. A READ's server budget stays under it
 * (FDB_EDGE_BUDGET_MS); a WRITE's job does too (30 s + the 20 s turn wait), but its route then records a version inline
 * (reading the whole file — #296 I7), which on a very large file can still pass it. Like the client's default, a
 * CLEARED timer (never AbortSignal.timeout's). An `outcome_unknown` answer still needs the grid to reload (I3).
 *
 * The trade: a request carrying its own signal also opts out of the GET stall retry (#155,
 * docs/2026-06-13_CF_RESPONSE_STALL.md) — a lost response on a Firebird GET now spins up to 100 s, not ~16 s. No 8 s
 * bound fits these routes: even a tiny file legitimately takes 30 s + the 20 s turn wait.
 */
const FDB_CLIENT_TIMEOUT_MS = 100_000

async function fdbCall<T>(path: string, init: Pick<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), FDB_CLIENT_TIMEOUT_MS)
  try {
    return await request<T>(path, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

export const firebirdAdapter: DbAdapter = {
  type: 'firebird',
  requiresAuth: true,

  async authenticate(fileId, user, password, rememberMe) {
    return fdbCall(`/api/fdb/${fileId}/authenticate`, {
      method: 'POST',
      body: JSON.stringify({ user, password, rememberMe }),
    })
  },

  async logout(fileId) {
    return request(`/api/fdb/${fileId}/logout`, { method: 'POST' })
  },

  async getSchema(fileId) {
    return fdbCall(`/api/fdb/${fileId}/schema`)
  },

  async getTable(fileId, tableName, limit, offset, opts) {
    const cols = columnSubsetQuery(opts?.columns)
    const qs = `?limit=${limit}&offset=${offset}${cols}`

    let res: any
    if (opts?.filters && opts.filters.length > 0) {
      res = await fdbCall(`/api/fdb/${fileId}/table/${encodeURIComponent(tableName)}${qs}`, {
        method: 'POST',
        body: JSON.stringify({ filters: opts.filters }),
      })
    } else {
      const where = opts?.rawWhere ? `&where=${encodeURIComponent(opts.rawWhere)}` : ''
      res = await fdbCall(`/api/fdb/${fileId}/table/${encodeURIComponent(tableName)}${qs}${where}`)
    }
    if (res.rows?.length > 0 && Array.isArray(res.rows[0])) {
      res.rows = transposeRows(res)
    }
    return res
  },

  async query(fileId, sql) {
    // a reader's SQL runs on a copy too (a writer's on the live file, answered sooner)
    const res: any = await fdbCall(`/api/fdb/${fileId}/query`, {
      method: 'POST',
      body: JSON.stringify({ sql }),
    })
    if (res.rows?.length > 0 && Array.isArray(res.rows[0])) {
      res.rows = transposeRows(res)
    }
    return res
  },

  async execute(fileId, sql) {
    return fdbCall(`/api/fdb/${fileId}/query`, {
      method: 'POST',
      body: JSON.stringify({ sql }),
    })
  },

  async cellUpdate(fileId, table, column, rowid, newValue, oldValue) {
    return fdbCall(`/api/fdb/${fileId}/cell`, {
      method: 'POST',
      body: JSON.stringify({ table, column, newValue, oldValue, pkValues: { __rowid: rowid } }),
    })
  },

  async cellSave(fileId, table, updates) {
    const fdbUpdates = updates.map(u => ({
      column: u.column,
      newValue: u.newValue,
      pkValues: { __rowid: u.rowid },
    }))
    return fdbCall(`/api/fdb/${fileId}/cell-save`, {
      method: 'POST',
      body: JSON.stringify({ table, updates: fdbUpdates }),
    })
  },

  async rowDelete(fileId, table, rowids) {
    return fdbCall(`/api/fdb/${fileId}/row-delete`, {
      method: 'POST',
      body: JSON.stringify({ table, pkRows: rowids.map(id => ({ __rowid: id })) }),
    })
  },

  async rowInsert(fileId, table, values) {
    return fdbCall(`/api/fdb/${fileId}/row-insert`, {
      method: 'POST',
      body: JSON.stringify({ table, values }),
    })
  },

  async tableDrop(fileId, table) {
    return fdbCall(`/api/fdb/${fileId}/table-drop`, {
      method: 'POST',
      body: JSON.stringify({ table }),
    })
  },

  async tableCreate(fileId, table, columns) {
    return fdbCall(`/api/fdb/${fileId}/table-create`, {
      method: 'POST',
      body: JSON.stringify({ table, columns }),
    })
  },

  async tableDuplicate(fileId, table, newName) {
    return fdbCall(`/api/fdb/${fileId}/table-duplicate`, {
      method: 'POST',
      body: JSON.stringify({ table, newName }),
    })
  },

  async tableModify(fileId, table, action, opts) {
    return fdbCall(`/api/fdb/${fileId}/table-modify`, {
      method: 'POST',
      body: JSON.stringify({ table, action, ...opts }),
    })
  },

  async getTableInfo(fileId, tableName) {
    return fdbCall(`/api/fdb/${fileId}/table-info/${encodeURIComponent(tableName)}`)
  },

  async exportTable(fileId, table, format, opts) {
    const res = await fetchRaw(`/api/fdb/${fileId}/export-table`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ table, format, columns: opts?.columns, where: opts?.where }),
    })
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export failed')
    return res.blob()
  },

  async flush(fileId) {
    // Save's last step: the server reads the whole file under its turn and records a version — longer than 30 s on a
    // large file, after every change already landed (a "failed" Save that wasn't)
    return fdbCall(`/api/fdb/${fileId}/flush`, { method: 'POST' })
  },

  async discard(fileId) {
    return request(`/api/fdb/${fileId}/discard`, { method: 'POST' })
  },
}
